import { useEffect } from 'react'
// @mui
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Drawer from '@mui/material/Drawer'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
// hooks
import { useResponsive } from 'src/hooks/use-responsive'
// hooks
import { useUserStore } from 'src/store/user'
// components
import Logo from 'src/components/logo'
import Scrollbar from 'src/components/scrollbar'
import { usePathname } from 'src/routes/hook'
import { NavSectionVertical } from 'src/components/nav-section'
//
import { NAV } from '../config-layout'
import { useNavData } from './config-navigation'
import { NavToggleButton } from '../_common'

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean
  onCloseNav: VoidFunction
}

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const { user } = useUserStore()

  const pathname = usePathname()

  const lgUp = useResponsive('up', 'lg')

  const navData = useNavData()

  useEffect(() => {
    if (openNav) {
      onCloseNav()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header com Logo */}
      <Box
        sx={{
          px: 3,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          mb: 2,
        }}
      >
        <Logo sx={{ mb: 1 }} />
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          DevBrasil Events
        </Typography>
      </Box>

      {/* Navegação */}
      <NavSectionVertical
        data={navData}
        config={{
          currentRole: user?.role || 'admin',
        }}
        sx={{ px: 1 }}
      />

      <Box sx={{ flexGrow: 1 }} />

      {/* Footer com informações do usuário */}
      <Box
        sx={{
          p: 2,
          m: 2,
          borderRadius: 2,
          bgcolor: 'background.neutral',
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant='caption' color='text.secondary' display='block'>
          Logado como:
        </Typography>
        <Typography variant='body2' fontWeight={600} color='text.primary'>
          {user?.name || 'Usuário'}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {user?.role === 'admin' ? 'Administrador' : 'Usuário'}
        </Typography>
      </Box>
    </Scrollbar>
  )

  return (
    <Box
      component='nav'
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      <NavToggleButton />

      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            boxShadow: (theme) => theme.customShadows.card,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  )
}
